import { useCallback, useEffect, useState } from 'react';

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $patchStyleText } from '@lexical/selection';
import { mergeRegister } from '@lexical/utils';
import {
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_LOW,
  LexicalCommand,
  createCommand,
} from 'lexical';
import { Dropdown } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import Button from 'components/button/Button';
import { RichTextEditorToolbarPluginProps } from 'components/editor/RichTextEditor.types';
import TextColorButtonPicker from './TextColorButtonPicker';

interface TextColorButtonProps {
  disabled: boolean;
  toolbarSettings: RichTextEditorToolbarPluginProps['toolbarSettings'];
}

export const SET_TEXT_COLOR_COMMAND: LexicalCommand<string> =
  createCommand<string>('SET_TEXT_COLOR_COMMAND');
export const SET_BACKGROUND_COLOR_COMMAND: LexicalCommand<string> =
  createCommand<string>('SET_BACKGROUND_COLOR_COMMAND');

const TextColorButton = ({
  disabled,
  toolbarSettings: { features },
}: TextColorButtonProps) => {
  const { t } = useTranslation();
  const [editor] = useLexicalComposerContext();

  const [lastForegroundColorUsed, setLastForegroundColorUsed] = useState('');

  const [lastBackgroundColorUsed, setLastBackgroundColorUsed] =
    useState('transparent');

  const applyTextColor = useCallback(
    (color: string) => {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $patchStyleText(selection, { color });
          setLastForegroundColorUsed(color);
        }
      });
    },
    [editor],
  );

  const applyBackgroundColor = useCallback(
    (color: string) => {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $patchStyleText(selection, { 'background-color': color });
          setLastBackgroundColorUsed(color);
        }
      });
    },
    [editor],
  );

  useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        SET_TEXT_COLOR_COMMAND,
        (color) => {
          applyTextColor(color);
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(
        SET_BACKGROUND_COLOR_COMMAND,
        (color) => {
          applyBackgroundColor(color);
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
    );
  }, [editor, applyTextColor, applyBackgroundColor]);

  if (!features?.textColor) {
    return null;
  }

  return (
    <Dropdown disabled={disabled} id="rich-text-editor-text-color">
      <Button
        aria-label={t('form.editor.buttons.recentColor')}
        bsStyle="default"
        className="toolbar-item toolbar-item--horizontal-dropdown"
        data-testid="toolbar-button-textColor"
        disabled={disabled}
        onClick={() => {
          applyBackgroundColor(lastBackgroundColorUsed);
          applyTextColor(lastForegroundColorUsed);
        }}
      >
        <span
          style={{
            backgroundColor: lastBackgroundColorUsed,
            color: lastForegroundColorUsed,
          }}
        >
          A
        </span>
      </Button>
      <Dropdown.Toggle className="toolbar-item" />
      <Dropdown.Menu>
        <div className="color-picker-column">
          <div>{t('form.editor.color.backgroundColor')}</div>
          <Button
            bsStyle="default"
            data-testid="toolbar-button-textColor-background-transparent"
            onClick={() => applyBackgroundColor('transparent')}
          >
            {t('form.editor.color.transparent')}
          </Button>
          <TextColorButtonPicker onClick={applyBackgroundColor} />
        </div>
        <div className="color-picker-column">
          <div>{t('form.editor.color.foregroundColor')}</div>
          <Button
            bsStyle="default"
            data-testid="toolbar-button-textColor-foreground-default"
            onClick={() => applyTextColor('')}
          >
            {t('form.editor.color.resetToDefault')}
          </Button>
          <TextColorButtonPicker onClick={applyTextColor} />
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default TextColorButton;
